import { createRouter, createWebHashHistory } from "vue-router";
import { addSearchFromRoute } from "@/shared/composables/useRecentSearches";
import type { RouteRecordRaw } from "vue-router";

import { authRoutes } from "./routes/auth.routes";
import { customerRoutes } from "./routes/customer.routes";
import { vendorRoutes } from "./routes/vendor.routes";
import { providerRoutes } from "./routes/provider.routes";
import { companyRoutes } from "./routes/company.routes";
import { sharedRoutes } from "./routes/shared.routes";
import { getActiveContext } from "@/modules/auth/services/auth.api";
import { getAuthToken, getContexts, getCustomer, setRequiresContextSelection } from "@/modules/auth/services/auth.storage";
import { getContextKind } from "@/modules/auth/services/auth.api";
import { isVendorMode } from "@/shared/utils/appMode";

const routes: RouteRecordRaw[] = [
  ...sharedRoutes,
  ...authRoutes,
  ...customerRoutes,
  ...vendorRoutes,
  ...providerRoutes,
  ...companyRoutes,
  {
    path: "/:pathMatch(.*)*",
    // changed: redirect by path to ensure root loads correctly on refresh
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
});

function isFirstLogin() {
  try {
    const customer: any = getCustomer<any>() || null;
    return customer?.first_login === true;
  } catch {
    return false;
  }
}

function isVendorProfileSetupRequired() {
  try {
    const activeContext: any = getActiveContext() || null;
    return activeContext?.profile_setup?.setup_required === true;
  } catch {
    return false;
  }
}

function getContextTypeMeta(matched: any[]) {
  return matched
    .map((r) => (r.meta as any)?.contextType)
    .find((m) => typeof m !== "undefined");
}

function applySharedAuthRules(to: any, next: any) {
  const matched = to.matched || [];
  const requiresAuth = matched.some((r: any) => (r.meta as any)?.requiresAuth);

  if (!requiresAuth) {
    next();
    return true;
  }

  const hasToken = Boolean(getAuthToken());
  if (!hasToken) {
    next();
    return true;
  }

  if (isVendorMode()) {
    next();
    return true;
  }
  const contexts = getContexts();
  if (Array.isArray(contexts) && contexts.length > 0 && to.name === "login.page") {
    next({ name: "context-selection.page" });
    return true;
  }

  const activeCtx = getActiveContext() || {};
  const activeType = (activeCtx.type || "").toString();

  const contextTypeMeta = getContextTypeMeta(matched);
  if (contextTypeMeta) {
    if (contextTypeMeta === activeType) {
      next();
    } else {
      setRequiresContextSelection();
      next({ name: "context-selection.page" });
    }
    return true;
  }

  next();
  return true;
}
// Customer-mode guard (runs only when app mode !== "vendor")
if (isVendorMode()) {

  // Vendor-mode guard (runs only when app mode === "vendor")
  router.beforeEach((to, from, next) => {
    const requiresProfileSetup = isVendorProfileSetupRequired();

    if (
      (requiresProfileSetup) &&
      to.name === "vendor.dashboard"
    ) {
      next({ name: "vendor.profile-setup" });
      return;
    }

    if (to.name === "login.page" && getAuthToken()) {
      next({ name: requiresProfileSetup ? "vendor.profile-setup" : "vendor.dashboard" });
      return;
    }
    applySharedAuthRules(to, next);
  });
} else {
  router.beforeEach((to, from, next) => {
    if (isFirstLogin()) {
      if (to.name === "vendor.dashboard") {
        next({ name: "vendor.profile-setup" });
        return;
      }
      if (to.name === "customer.dashboard") {
        next({ name: "change-password.page" });
        return;
      }
    }

    if (to.name === "login.page" && getAuthToken()) {
      const kind = getContextKind(getActiveContext() as any);
      if (kind === "customer") {
        next({ name: "customer.dashboard" });
        return;
      }
      if (kind === "vendor") {
        next({ name: "vendor.dashboard" });
        return;
      }
      next({ name: "marketplace.home" });
      return;
    }

    applySharedAuthRules(to, next);
  });
}

router.afterEach((to) => {
  addSearchFromRoute(to);
});

export default router;
